// =============================================================================
// manHoursExport.ts — T-369
// Excel + CSV export for the man-hours-by-project report. Client-facing: a
// clean Summary sheet (one row per project) plus a By-person detail sheet, both
// stamped with the period + active filters so a client knows exactly what the
// numbers cover (e.g. "total man-hours on Amplitel in June").
// =============================================================================

import * as XLSX from "xlsx";
import { dated } from "@/lib/exportCsv";

export interface ManHoursProjectRow {
  project_code: string;
  project_name: string;
  site_name:    string;
  client:       string;
  carrier:      string;
  asset_owner:  string;
  people:       number;
  days:         number;
  hours_std:    number;
  hours_th:     number;
  hours_dt:     number;
  total_hours:  number;
}

export interface ManHoursPersonRow {
  name:            string;
  worker_type:     string;   // Field / Directors / Labour hire / Subbies
  project_code:    string;
  project_name:    string;
  client:          string;
  days:            number;
  total_hours:     number;
}

export interface ManHoursMeta {
  start:       string;
  end:         string;
  filterLine:  string;   // e.g. "Client: Amplitel · Carrier: NBN"
}

const N = (n: number) => Math.round(n * 100) / 100;

export function exportManHoursXlsx(
  projects: ManHoursProjectRow[],
  people: ManHoursPersonRow[],
  meta: ManHoursMeta,
): void {
  const wb = XLSX.utils.book_new();

  // ---- Summary sheet -------------------------------------------------------
  const tStd = N(projects.reduce((a, p) => a + p.hours_std, 0));
  const tTh  = N(projects.reduce((a, p) => a + p.hours_th, 0));
  const tDt  = N(projects.reduce((a, p) => a + p.hours_dt, 0));
  const tTot = N(projects.reduce((a, p) => a + p.total_hours, 0));

  const summary: (string | number)[][] = [
    ["Coretel Group — Man-hours by project"],
    [`Period: ${meta.start} to ${meta.end}`],
    [meta.filterLine || "All clients, carriers and asset owners"],
    [],
    ["Project", "Name", "Site", "Client", "Carrier", "Asset owner", "People", "On-site days", "Ordinary", "OT 1.5x", "OT 2x", "Total hrs"],
    ...projects.map((p) => [
      p.project_code, p.project_name, p.site_name, p.client, p.carrier, p.asset_owner,
      p.people, p.days, N(p.hours_std), N(p.hours_th), N(p.hours_dt), N(p.total_hours),
    ]),
    [],
    ["TOTAL", "", "", "", "", "", "", "", tStd, tTh, tDt, tTot],
  ];
  const ws1 = XLSX.utils.aoa_to_sheet(summary);
  ws1["!cols"] = [
    { wch: 16 }, { wch: 26 }, { wch: 22 }, { wch: 22 }, { wch: 12 }, { wch: 16 },
    { wch: 8 }, { wch: 12 }, { wch: 10 }, { wch: 9 }, { wch: 9 }, { wch: 11 },
  ];
  XLSX.utils.book_append_sheet(wb, ws1, "Summary");

  // ---- By-person detail sheet ---------------------------------------------
  const detail: (string | number)[][] = [
    ["Man-hours — by person"],
    [`Period: ${meta.start} to ${meta.end}`],
    [],
    ["Name", "Worker type", "Project", "Project name", "Client", "On-site days", "Total hrs"],
    ...people.map((r) => [
      r.name, r.worker_type, r.project_code, r.project_name, r.client, r.days, N(r.total_hours),
    ]),
  ];
  const ws2 = XLSX.utils.aoa_to_sheet(detail);
  ws2["!cols"] = [
    { wch: 24 }, { wch: 12 }, { wch: 16 }, { wch: 26 }, { wch: 22 }, { wch: 12 }, { wch: 11 },
  ];
  XLSX.utils.book_append_sheet(wb, ws2, "By person");

  XLSX.writeFile(wb, `${dated("man_hours_by_project")}.xlsx`);
}
